import { Component, OnInit, Inject, Optional } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';

import { PropertyService } from '../../services/property.service';
import { NavigationContextService } from '../../services/navigation-context.service';

@Component({
  selector: 'app-modify-lease',
  templateUrl: './modify-lease.component.html',
  styleUrls: ['./modify-lease.component.scss']
})
export class ModifyLeaseComponent implements OnInit {
  lease: any = null;
  property: any;
  loading = true;
  error: string = null;
  readOnly = false;
  returnPropertyId: string = null;

  constructor(private propertyService: PropertyService,
              @Optional() public dialogRef: MatDialogRef<ModifyLeaseComponent>,
              @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
              private route: ActivatedRoute,
              private router: Router,
              private navContext: NavigationContextService) { }

  get isDialog(): boolean {
    return !!this.dialogRef;
  }

  ngOnInit() {
    if (this.data && this.data.lease) {
      this.property = this.data.property || { id: this.data.lease.propertyId };
      this.returnPropertyId = this.property && this.property.id;
      const leaseId = this.data.lease.id;
      // Prefer a fresh lease payload so dialog view matches current API data
      if (leaseId) {
        this.loadLeaseById(leaseId, this.returnPropertyId);
      } else {
        this.applyLoadedLease(this.data.lease);
        this.loading = false;
      }
      return;
    }

    const propertyId = this.route.snapshot.queryParamMap.get('propertyId');
    const leaseId = this.route.snapshot.queryParamMap.get('leaseId');
    this.returnPropertyId = propertyId;

    if (leaseId) {
      this.loadLeaseById(leaseId, propertyId);
    } else if (propertyId) {
      this.loadActiveLeaseForProperty(propertyId);
    } else {
      this.error = 'No lease or property specified.';
      this.loading = false;
    }
  }

  private loadLeaseById(leaseId: string, propertyId?: string) {
    this.loading = true;
    this.error = null;
    this.propertyService.getLeaseById(leaseId).then((lease) => {
      if (!lease) {
        this.error = 'Lease not found.';
        this.loading = false;
        return;
      }
      const resolvedPropertyId = propertyId || lease.propertyId || (lease.property && lease.property.id);
      this.returnPropertyId = resolvedPropertyId;
      if (resolvedPropertyId && !(this.property && this.property.addressLine1)) {
        return this.propertyService.getPublicProperty(resolvedPropertyId).then((property) => {
          this.property = property;
          this.applyLoadedLease(lease);
          this.loading = false;
        });
      }
      if (!this.property) {
        this.property = lease.property || { id: lease.propertyId };
      }
      this.applyLoadedLease(lease);
      this.loading = false;
    }).catch((err) => {
      console.error('Error loading lease:', err);
      this.error = (err && err.error && err.error.error) || 'Failed to load lease.';
      this.loading = false;
    });
  }

  private loadActiveLeaseForProperty(propertyId: string) {
    this.loading = true;
    this.error = null;
    this.propertyService.getPublicProperty(propertyId).then((property) => {
      this.property = property;
      return this.propertyService.getActiveLease(property);
    }).then((lease) => {
      if (!lease) {
        this.error = 'No active lease found for this property.';
        this.loading = false;
        return;
      }
      this.applyLoadedLease(lease);
      this.loading = false;
    }).catch((err) => {
      console.error('Error loading active lease:', err);
      this.error = (err && err.error && err.error.error) || 'Failed to load lease.';
      this.loading = false;
    });
  }

  private normalizeDateForInput(value: any): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.substring(0, 10);
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      return null;
    }
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private applyLoadedLease(lease: any) {
    this.lease = JSON.parse(JSON.stringify(lease || {}));
    if (!this.lease.type) {
      if (!this.lease.startDate && !this.lease.endDate) {
        this.lease.type = 'M2M';
      } else {
        this.lease.type = 'TERM';
      }
    }
    this.lease.startDate = this.normalizeDateForInput(this.lease.startDate);
    this.lease.endDate = this.normalizeDateForInput(this.lease.endDate);
    this.readOnly = String(this.lease.status || '').toUpperCase() === 'EXPIRED';
  }

  get shortPageTitle(): string {
    if (this.readOnly) {
      return 'View lease';
    }
    return 'Edit lease';
  }

  get pageTitle(): string {
    const address = this.property && this.property.addressLine1
      ? this.property.addressLine1
      : 'property';
    if (this.readOnly) {
      return `View lease for ${address}`;
    }
    return `View / edit lease for ${address}`;
  }

  onCancel() {
    this.navigateBack();
  }

  onSave() {
    if (this.readOnly) {
      return;
    }
    const target = this.property || (this.data && this.data.property);
    if (!target || !this.lease) {
      alert('Cannot save: property or lease is missing.');
      return;
    }
    this.propertyService.modifyLease(target, this.lease).then(() => {
      this.navigateBack();
    }).catch((error) => {
      console.error('Error updating lease:', error);
      const errorMessage = (error && error.error && error.error.error)
        ? error.error.error
        : (error.message || 'Failed to update lease. Please try again.');
      alert(errorMessage);
    });
  }

  close() {
    this.navigateBack();
  }

  onViewDocument(doc: any) {
    if (!doc || !doc.propertyId || !doc.documentId) {
      return;
    }
    const queryParams: any = {
      fileName: doc.fileName || 'lease.pdf'
    };
    if (doc.leaseId) {
      queryParams.leaseId = doc.leaseId;
    }
    // Close the dialog first so the PDF screen is not trapped under the overlay.
    if (this.dialogRef) {
      this.dialogRef.close(this.lease);
    }
    this.router.navigate(
      ['/lease-document', doc.propertyId, doc.documentId],
      { queryParams }
    );
  }

  private navigateBack() {
    if (this.dialogRef) {
      this.dialogRef.close(this.lease);
      return;
    }
    const propertyId = this.returnPropertyId
      || (this.property && this.property.id)
      || this.navContext.propertyId;
    this.navContext.goToManageLeases(propertyId);
  }
}
